/* eslint-disable */
import { useEffect, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Session 57 · Admin → Stripe Connect Live Status.
 *
 * One-glance view of every venue's Stripe Connect state, with a Refresh
 * Status button that immediately re-fetches from Stripe and mirrors the
 * capability flags into the DB. Solves the "Rec-Mode is actually ready but
 * Métier still thinks it's blocked" class of bugs — the admin gets the
 * source of truth back within a click.
 *
 * Complements the existing Session 29 `AdminVenueStripe` tab (which handles
 * onboarding invites + reconciliation) by focusing on live capability sync.
 */
const REQ_LABELS = {
  "business_profile.mcc": "Merchant category code",
  "business_profile.url": "Business website URL",
  "business_type": "Business type",
  "external_account": "Bank account for payouts",
  "individual.dob.day": "Date of birth · day",
  "individual.dob.month": "Date of birth · month",
  "individual.dob.year": "Date of birth · year",
  "individual.first_name": "First name",
  "individual.last_name": "Last name",
  "individual.address.line1": "Home address",
  "individual.address.postal_code": "Zip code",
  "individual.email": "Personal email",
  "individual.phone": "Phone number",
  "individual.ssn_last_4": "Last 4 of SSN",
  "individual.id_number": "Full SSN (identity verification)",
  "individual.verification.document": "Government ID upload",
  "individual.verification.additional_document": "Additional ID document",
  "tos_acceptance.date": "Accept Stripe terms of service",
  "tos_acceptance.ip": "Accept Stripe terms of service (from a device)",
};
const humanize = (code) => REQ_LABELS[code] || code.replace(/\./g, " → ").replace(/_/g, " ");

export default function AdminStripeStatus() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [toast, setToast] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/stripe/venues");
      setRows(r.data?.venues || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const showToast = (kind, text) => {
    setToast({ kind, text });
    setTimeout(() => setToast(null), 6000);
  };

  const refresh = async (row) => {
    const id = row.workspace_id || row.slug;
    setBusy(id);
    try {
      let res;
      if (row.kind === "workspace") {
        res = await api.post("/admin/stripe/refresh-status", { workspace_id: row.workspace_id });
      } else {
        res = await api.post("/admin/stripe/refresh-public-listing", { slug: row.slug });
      }
      const v = res.data?.verification || {};
      if (!v.verified) {
        showToast("error", v.error || "Stripe refresh failed — check API key + account permissions.");
      } else if (res.data.online_bookable) {
        showToast("ok", `${row.name}: Stripe ready — online booking now enabled.`);
      } else {
        const dues = (v.requirements?.currently_due || []).length;
        showToast("warn", `${row.name}: refreshed — ${dues ? dues + " requirement(s) still due at Stripe" : "capability still disabled"}. See details.`);
      }
      await load();
      setExpanded((e) => ({ ...e, [id]: true }));
    } catch (e) {
      showToast("error", e?.response?.data?.detail || "Refresh failed.");
    } finally { setBusy(null); }
  };

  if (!user || user.role !== "admin") {
    return <div style={{ padding: 40 }}>Admin access required.</div>;
  }

  const summary = {
    total: rows.length,
    ready: rows.filter((r) => r.online_bookable).length,
    blocked: rows.filter((r) => !r.online_bookable && r.stripe_account_id).length,
    no_account: rows.filter((r) => !r.stripe_account_id).length,
  };

  return (
    <div data-testid="admin-stripe-status" style={{ minHeight: "100vh", background: "#FAF8F3", padding: "40px 24px 80px", fontFamily: "Helvetica,Arial,sans-serif", color: "#1B2340" }}>
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 20 }}>
          <div>
            <div style={{ letterSpacing: "0.22em", color: "#B8913A", fontSize: 11, fontWeight: 700 }}>MÉTIER · ADMIN</div>
            <h1 style={{ fontFamily: "Georgia,serif", fontWeight: 500, fontSize: 34, margin: "6px 0 0" }}>Stripe Connect · Live Status</h1>
            <p style={{ fontSize: 14, color: "rgba(27,35,64,0.6)", margin: "6px 0 0" }}>
              Every venue&#39;s Stripe capability state. Click <strong>Refresh</strong> to re-fetch from Stripe and clear stale local flags.
            </p>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <Link to="/admin" style={ghostLink}>← Admin</Link>
            <Link to="/admin/alpha" style={ghostLink}>Alpha Dashboard →</Link>
          </div>
        </div>

        <div style={grid4}>
          <StatCard label="Total venues" value={summary.total} />
          <StatCard label="Stripe ready" value={summary.ready} color="#1B4634" />
          <StatCard label="Blocked" value={summary.blocked} color="#7C5B10" />
          <StatCard label="No account" value={summary.no_account} color="#7A1B22" />
        </div>

        <div style={{ marginTop: 22, background: "#fff", borderRadius: 12, border: "1px solid rgba(27,35,64,0.08)", overflow: "hidden" }}>
          <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(27,35,64,0.06)", display: "flex", justifyContent: "space-between" }}>
            <strong>All venues</strong>
            <span style={{ fontSize: 12, color: "rgba(27,35,64,0.55)" }}>{rows.length} record{rows.length === 1 ? "" : "s"}</span>
          </div>
          {loading ? (
            <div style={{ padding: 40, textAlign: "center", color: "rgba(27,35,64,0.5)" }}>Loading…</div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "rgba(27,35,64,0.5)" }}>No venues in DB.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#FAF8F3", textAlign: "left", color: "rgba(27,35,64,0.7)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  <th style={th}>Venue</th>
                  <th style={th}>Kind</th>
                  <th style={th}>Account</th>
                  <th style={th}>Charges</th>
                  <th style={th}>Payouts</th>
                  <th style={th}>Details</th>
                  <th style={th}>Bookable</th>
                  <th style={th}>Last sync</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const id = row.workspace_id || row.slug;
                  const isOpen = expanded[id];
                  const dueList = row.stripe_requirements?.currently_due || [];
                  const eventuallyDue = row.stripe_requirements?.eventually_due || [];
                  const pastDue = row.stripe_requirements?.past_due || [];
                  const disabledReason = row.stripe_requirements?.disabled_reason;
                  const hasIssues = dueList.length + pastDue.length > 0 || disabledReason;
                  return (
                    <Fragment key={id}>
                      <tr data-testid={`stripe-status-row-${id}`} style={{ borderTop: "1px solid rgba(27,35,64,0.06)" }}>
                        <td style={td}>
                          <div style={{ fontWeight: 600 }}>{row.name || "—"}</div>
                          <div style={{ fontSize: 12, color: "rgba(27,35,64,0.55)" }}>{row.city || "—"} · <code>{row.slug || row.workspace_id}</code></div>
                        </td>
                        <td style={td}>
                          <span style={row.kind === "workspace" ? pillKindWs : pillKindPl}>{row.kind}</span>
                        </td>
                        <td style={td}>
                          {row.stripe_account_id
                            ? <code style={{ fontSize: 11 }}>{row.stripe_account_id}</code>
                            : <span style={pillNone}>Not attached</span>}
                        </td>
                        <td style={td}>{row.stripe_charges_enabled ? "✅" : "—"}</td>
                        <td style={td}>{row.stripe_payouts_enabled ? "✅" : "—"}</td>
                        <td style={td}>{row.stripe_details_submitted ? "✅" : "—"}</td>
                        <td style={td}>
                          {row.online_bookable
                            ? <span style={pillReady}>Stripe Ready</span>
                            : <span style={pillBlocked}>{(row.status || "blocked").replace(/_/g, " ")}</span>}
                        </td>
                        <td style={td}>
                          <span style={{ fontSize: 12, color: "rgba(27,35,64,0.55)" }}>{fmt(row.stripe_last_verified_at)}</span>
                        </td>
                        <td style={td}>
                          <button
                            data-testid={`refresh-stripe-${id}`}
                            disabled={busy === id || !row.stripe_account_id}
                            onClick={() => refresh(row)}
                            style={{ ...btnPrimary, opacity: (busy === id || !row.stripe_account_id) ? 0.5 : 1 }}
                          >
                            {busy === id ? "Refreshing…" : "Refresh"}
                          </button>
                          <button
                            onClick={() => setExpanded((e) => ({ ...e, [id]: !e[id] }))}
                            style={{ ...btnSecondary, marginLeft: 6 }}
                            data-testid={`details-${id}`}
                          >{isOpen ? "Hide" : "Details"}</button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr style={{ background: "#FAF8F3" }}>                          <td colSpan={9} style={{ padding: "16px 24px", fontSize: 13 }}>
                            {!hasIssues && row.online_bookable && (
                              <div style={{ color: "#1B4634", fontSize: 13 }}>
                                <strong>✅ All good.</strong> Charges, payouts, and details are all enabled. No outstanding requirements at Stripe.
                              </div>
                            )}
                            {!hasIssues && !row.online_bookable && (
                              <div style={{ color: "rgba(27,35,64,0.6)" }}>
                                No cached requirements. Click <strong>Refresh</strong> to fetch the latest state from Stripe.
                              </div>
                            )}
                            {disabledReason && (
                              <div style={{ padding: "8px 12px", borderRadius: 8, background: "#FDE8EA", color: "#7A1B22", marginBottom: 10, fontSize: 12 }}>
                                <strong>Disabled by Stripe:</strong> {humanize(disabledReason)}
                              </div>
                            )}
                            {pastDue.length > 0 && (
                              <div style={{ marginBottom: 12 }}>
                                <div style={{ fontSize: 11, letterSpacing: "0.14em", color: "#7A1B22", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Past Due — Blocking Now</div>
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                  {pastDue.map((r) => <li key={r}>{humanize(r)}</li>)}
                                </ul>
                              </div>
                            )}
                            {dueList.length > 0 && (
                              <div style={{ marginBottom: 12 }}>
                                <div style={{ fontSize: 11, letterSpacing: "0.14em", color: "#7C5B10", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Currently Due — Host Must Complete</div>
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                  {dueList.map((r) => <li key={r}>{humanize(r)}</li>)}
                                </ul>
                              </div>
                            )}
                            {eventuallyDue.length > 0 && (
                              <div style={{ marginBottom: 8 }}>
                                <div style={{ fontSize: 11, letterSpacing: "0.14em", color: "rgba(27,35,64,0.55)", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Eventually Due</div>
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                  {eventuallyDue.map((r) => <li key={r}>{humanize(r)}</li>)}
                                </ul>
                              </div>
                            )}
                            {row.stripe_account_id && hasIssues && (
                              <a
                                href={`https://dashboard.stripe.com/${row.stripe_account_id}`}
                                target="_blank"
                                rel="noreferrer"
                                style={{ display: "inline-block", marginTop: 6, padding: "8px 16px", background: "#635BFF", color: "#fff", borderRadius: 999, fontSize: 12, textDecoration: "none", fontWeight: 600 }}
                              >
                                Resume onboarding at Stripe →
                              </a>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ marginTop: 22, background: "#fff", borderRadius: 12, border: "1px solid rgba(184,145,58,0.35)", padding: "16px 20px", fontSize: 13, color: "rgba(27,35,64,0.75)" }}>
          <strong style={{ color: "#B8913A" }}>How the booking gate works.</strong> Checkout is blocked when any of <code>charges_enabled</code> / <code>payouts_enabled</code> / <code>details_submitted</code> is <code>false</code>. Even after the host completes Stripe onboarding, Métier&#39;s local flags stay stale until we re-fetch. Click <strong>Refresh</strong> on any row to sync from Stripe live.
        </div>
      </div>

      {toast && (
        <div style={{
          position: "fixed", bottom: 24, right: 24, padding: "12px 18px",
          background: toast.kind === "ok" ? "#1B4634" : toast.kind === "warn" ? "#7C5B10" : "#7A1B22",
          color: "#fff", borderRadius: 12, fontSize: 13, maxWidth: 460,
          boxShadow: "0 10px 30px rgba(0,0,0,0.2)", zIndex: 99,
        }}>{toast.text}</div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{ background: "#fff", padding: "18px 20px", borderRadius: 12, border: "1px solid rgba(27,35,64,0.08)" }}>
      <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(27,35,64,0.55)", fontWeight: 600 }}>{label}</div>
      <div style={{ fontFamily: "Georgia,serif", fontSize: 28, fontWeight: 500, marginTop: 6, color: color || "#1B2340" }}>{value}</div>
    </div>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return "—"; }
}

const grid4 = { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 };
const ghostLink = { fontSize: 13, color: "#B8913A", textDecoration: "none", fontWeight: 600 };
const th = { padding: "10px 14px" };
const td = { padding: "12px 14px", verticalAlign: "top" };
const btnPrimary = { padding: "6px 14px", background: "#1B2340", color: "#fff", border: "none", borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer" };
const btnSecondary = { padding: "6px 12px", background: "#fff", color: "#1B2340", border: "1px solid rgba(27,35,64,0.2)", borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer" };
const pillReady = { display: "inline-block", padding: "2px 8px", background: "#DFEEE1", color: "#1B4634", fontSize: 11, borderRadius: 999, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" };
const pillBlocked = { display: "inline-block", padding: "2px 8px", background: "#FFF3D6", color: "#7C5B10", fontSize: 11, borderRadius: 999, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" };
const pillNone = { display: "inline-block", padding: "2px 8px", background: "#F3E4E4", color: "#7A1B22", fontSize: 11, borderRadius: 999, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" };
const pillKindWs = { display: "inline-block", padding: "2px 8px", background: "#E4EAF3", color: "#1B2340", fontSize: 10, borderRadius: 6, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" };
const pillKindPl = { display: "inline-block", padding: "2px 8px", background: "#F3EEDF", color: "#8B6A22", fontSize: 10, borderRadius: 6, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" };
